import { useEffect, useState } from 'react';

type SearchState<T> = {
  query: string;
  results: T[];
  status: 'idle' | 'loading' | 'success' | 'error';
};

/** A shared catalog request may finish after its consumer changes query or closes. */
export function useDebouncedSearch<T>(
  query: string,
  search: (query: string) => Promise<T[]>,
  delay = 350,
  enabled = true,
) {
  const normalizedQuery = enabled ? query.trim() : '';
  const [state, setState] = useState<SearchState<T>>({ query: '', results: [], status: 'idle' });

  useEffect(() => {
    let active = true;
    if (!normalizedQuery) {
      setState({ query: '', results: [], status: 'idle' });
      return () => { active = false; };
    }
    setState({ query: normalizedQuery, results: [], status: 'loading' });
    const timer = setTimeout(async () => {
      try {
        const results = await search(normalizedQuery);
        if (active) setState({ query: normalizedQuery, results, status: 'success' });
      } catch {
        if (active) setState({ query: normalizedQuery, results: [], status: 'error' });
      }
    }, delay);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [normalizedQuery, search, delay]);

  // Hide the previous query even before effect cleanup has run.
  if (!normalizedQuery) return { results: [] as T[], loading: false, searched: false, error: false };
  const current = state.query === normalizedQuery;
  return {
    results: current ? state.results : [],
    loading: !current || state.status === 'loading',
    searched: current && state.status === 'success',
    error: current && state.status === 'error',
  };
}
